import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  LayoutDashboard, FolderOpen, MessageCircle,
  LogOut, User, Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";


export const ClientLayout = ({ children }) => {
  const { user, logout, aiFilingEnabled } = useAuth();
  const location = useLocation();
  const navigate  = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  // aiFilingEnabled from AuthContext — already resolved at app startup
  // No async call here, no flash

  const navItems = [
    { path: "/dashboard", label: "Dashboard", icon: <LayoutDashboard size={20} /> },
    { path: "/plans",     label: "Tax Plans", icon: <FolderOpen size={20} /> },
    ...(aiFilingEnabled ? [{ path: "/ai-filing", label: "AI Filing", icon: <Sparkles size={20} /> }] : []),
    { path: "/messages",  label: "Messages",  icon: <MessageCircle size={20} /> },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const res = await api.get("/messages/unread-count");
        setUnreadCount(res.data.unread_count || 0);
      } catch (err) {
        console.error("Failed to fetch unread count", err);
      }
    };
    fetchUnreadCount();
  }, [location.pathname]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--secondary)" }}>
      {/* Header */}
      <header className="glass-header px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: "var(--primary)" }}
            >
              <img src="/mooltax-logo.png" alt="MoolTax" style={{ height: "32px", width: "auto" }} />
            </div>
            <span
              className="text-xl font-bold"
              style={{ fontFamily: "Fraunces, serif", color: "var(--primary)" }}
            >
              MoolTax
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  location.pathname === item.path ? "bg-primary text-white" : "hover:bg-muted"
                }`}
                style={location.pathname === item.path ? { backgroundColor: "var(--primary)" } : {}}
                data-testid={`nav-${item.path.slice(1)}`}
              >
                {item.icon}
                <span>{item.label}</span>
                {item.path === "/messages" && unreadCount > 0 && (
                  <span
                    className="ml-1 min-w-[20px] h-5 px-1 rounded-full text-xs flex items-center justify-center text-white"
                    style={{ backgroundColor: "var(--accent)" }}
                  >
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "var(--muted)" }}
              >
                <User size={18} />
              </div>
              <span className="hidden md:block font-medium">{user?.name}</span>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-btn">
              <LogOut size={20} />
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile Nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t flex justify-around py-3 z-50"
        style={{ borderColor: "var(--border)" }}
      >
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex flex-col items-center gap-1 px-3 py-1 rounded-lg ${
              location.pathname === item.path ? "text-accent" : "opacity-60"
            }`}
            style={location.pathname === item.path ? { color: "var(--accent)" } : {}}
          >
            <div className="relative flex items-center justify-center">
              {item.icon}
              {item.path === "/messages" && unreadCount > 0 && (
                <span
                  className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] flex items-center justify-center text-white"
                  style={{ backgroundColor: "var(--accent)" }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
            <span className="text-xs font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-6 py-8 pb-24 md:pb-8">
        {children}
      </main>
    </div>
  );
};